<script lang="ts">
    import SeparatedList from '@amp/web-app-components/src/components/helpers/SeparatedList.svelte';
    import { createEventDispatcher } from 'svelte';
    import type { Link } from './types';

    const dispatch = createEventDispatcher();

    /**
     * The main difference between Classical's WorkHeader and Music's ComposerHeader is the data shown within.
     * Classical shows the composer name first, then the Work name second.  Both are linkable.
     * Music shows the Work name first, with "By <ComposerName>" second.  Only the composer name(s) are linkable.
     *
     * Music also has a concept of separating compooser and artist links into two separate lists on the same line.
     * See: https://music.apple.com/us/album/gershwin-rhapsody-in-blue-an-american-in-paris/594512997
     *
     * To accomodate this behavior, any separate links that need to exist within the second line can be passed
     * in with the additionalSecondRowLinks property.  Otherwise, for the sake of generalizing a shared template,
     * we will use general firstRowLinks and secondRowLinks here to indicate where the links should appear.
     */
    export let isPlayable: boolean = false;
    export let firstRowText: string | null = null;
    export let secondRowText: string | null = null;
    export let firstRowLinks: Link[] = [];
    export let secondRowLinks: Link[] = [];
    export let additionalSecondRowLinks: Link[] = [];
    export let isoDuration: string | null = null;
    export let formattedDuration: string | null = null;
    export let showWorkDuration: boolean = true;
    export let clickComponent: any | null = null;
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;

    $: showFirstRow = firstRowLinks?.length || !!firstRowText;
    $: showSecondRow = secondRowLinks?.length || !!secondRowText;
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
    class="composer-header songs-list-row songs-list-row--works-list-header"
    data-testid="work-header"
    on:dblclick={() => dispatch('workHeaderPlayAction')}
    role="row"
    tabindex="-1"
>
    <div class="songs-list__col songs-list__col--prefix" role="gridcell">
        {#if isPlayable && $$slots['work-header-play-button']}
            <slot name="work-header-play-button" />
        {/if}
    </div>
    <div
        role="gridcell"
        class="songs-list__col songs-list__col--song songs-list__col--work-header"
    >
        {#if showFirstRow}
            <span
                class="composer-header__first-row"
                data-testid="work-header-first-row"
            >
                {#if firstRowLinks && firstRowLinks.length && !!clickComponent}
                    <span>
                        <SeparatedList
                            items={firstRowLinks}
                            {translateFn}
                            let:item={firstRowLink}
                        >
                            <!-- TODO: rdar://89617374 (JMOTW: Tracklist: LTR and RTL layout) -->
                            <span>
                                <svelte:component
                                    this={clickComponent}
                                    action={firstRowLink.segue}
                                >
                                    {firstRowLink.title}
                                </svelte:component>
                            </span>
                        </SeparatedList>
                    </span>
                {:else}
                    {firstRowText}
                {/if}
            </span>
        {/if}
        {#if showSecondRow}
            <div
                class="songs-list-row__by-line songs-list-row__by-line--work"
                data-testid="work-header-second-row"
            >
                {#if secondRowLinks && secondRowLinks.length && !!clickComponent}
                    <span>
                        {#if $$slots['work-links-leading-text']}
                            <slot name="work-links-leading-text" />
                        {/if}
                        <SeparatedList
                            items={secondRowLinks}
                            {translateFn}
                            let:item={secondRowLink}
                        >
                            <!-- TODO: rdar://89617374 (JMOTW: Tracklist: LTR and RTL layout) -->
                            <span>
                                <svelte:component
                                    this={clickComponent}
                                    action={secondRowLink.segue}
                                >
                                    {secondRowLink.title}
                                </svelte:component>
                            </span>
                        </SeparatedList>
                    </span>
                {/if}
                {#if additionalSecondRowLinks && additionalSecondRowLinks.length && !!clickComponent}
                    <span>
                        <SeparatedList
                            items={additionalSecondRowLinks}
                            {translateFn}
                            let:item={additionalSecondRowLink}
                        >
                            <!-- TODO: rdar://89617374 (JMOTW: Tracklist: LTR and RTL layout) -->
                            <span>
                                <svelte:component
                                    this={clickComponent}
                                    action={additionalSecondRowLink.segue}
                                >
                                    {additionalSecondRowLink.title}
                                </svelte:component>
                            </span>
                        </SeparatedList>
                    </span>
                {/if}
                {#if secondRowText && !secondRowLinks?.length}
                    {secondRowText}
                {/if}
            </div>
        {/if}
    </div>
    <div
        role="gridcell"
        class="songs-list__col songs-list__col--time songs-list__header-col songs-list__header-col--time"
    >
        <div class="songs-list-row__controls">
            <div class="songs-list-row__add-to-library">
                {#if $$slots['work-header-add-to-library']}
                    <slot name="work-header-add-to-library" />
                {/if}
            </div>

            {#if showWorkDuration && formattedDuration && isoDuration}
                <time
                    datetime={isoDuration}
                    class="songs-list-row__length"
                    data-testid="work-header-duration"
                >
                    {formattedDuration}
                </time>
            {/if}

            <div class="songs-list-row__context-menu">
                {#if $$slots['work-header-context-menu']}
                    <slot name="work-header-context-menu" />
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/margin-gutter' as *;
    @use '@amp/web-shared-styles/app/core/mixins/tabular-numbers' as *;

    // TODO: rdar://89617186 (JMOTW: UXD: Tracklist: investigate how we can have shared styling between different files)
    @use './styles/shared.scss' as *;

    .composer-header {
        @include margin-gutter(0, 13px);

        :global(.songs-list:hover) & {
            background-color: var(--tracklistHoverColor);
        }

        :global(.songs-list) & {
            &:hover {
                background-color: var(--tracklistWorksTrackHoverColor);
            }
        }

        :global(.play-button) {
            opacity: 0;
        }

        &:hover,
        &:focus-within {
            :global(.play-button) {
                opacity: 1;
            }
        }
    }

    .songs-list__col--work-header {
        padding-inline-start: 40px;

        @include line-clamp;

        // Force UI ltr/rtl alignment
        // Forcing text-aligment for use with `dir="auto"`
        :global(html:not([dir='rtl']) [dir='auto']) & {
            padding-right: 0;
            padding-left: $songs-list-play-button-area-footprint;
            text-align: left;
        }

        :global(html[dir='rtl'] [dir='auto']) & {
            padding-right: $songs-list-play-button-area-footprint;
            padding-left: 0;
            text-align: right;
        }
    }

    .songs-list-row__by-line--work {
        @include overflow-bleed($focus-size);
        // Space to spec, but compensate for focus ring overflow bleed
        margin-top: 1px - $focus-size; // stylelint-disable-line order/order

        @include line-clamp;

        // Adds em-dash and spaces between segments of the by-line
        > *:not(:last-child)::after {
            content: '\2004\2014\2004';
            display: inline;

            @include rtl {
                // Recommendation from the RTL team: Make the em-dash strongly RTL. It's weak
                // directionality may cause the line to become partially LTR in GoldenGateE Safari.
                content: '\2004\200F\2014\2004';
            }
        }
    }

    .composer-header__first-row {
        font: var(--body-emphasized-tall);
    }

    .composer-header__second-row {
        font: var(--body);
    }

    .songs-list-row--works-list-header {
        height: $songs-list-row-height-two-lines;
    }

    .songs-list-row__controls {
        display: inline-grid;
        grid-template-areas: 'add-to-library duration context-menu';
        grid-template-columns: var(
            --songs-row-controls-columns,
            auto auto auto
        );
        align-items: center;
        position: relative;
    }

    .songs-list-row__add-to-library {
        grid-area: add-to-library;
    }

    .songs-list-row__length {
        grid-area: duration;

        @include tabular-numbers;
    }

    .songs-list-row__context-menu {
        grid-area: context-menu;
    }

    .songs-list-row__length + .songs-list-row__context-menu {
        margin-inline-start: $songs-list-row-context-menu-spacer;
    }

    .songs-list__col--prefix {
        --playButtonIconColor: var(--keyColor);
        overflow: visible;
        width: var(--bodyGutter);

        :global(.play-button) {
            margin-inline-start: 12px;
        }
    }
</style>
